import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import debounce from 'lodash.debounce'

import axios from 'axios'

const fetchGroupedLiveSearch = async ({ queryKey, signal }) => {
  const [, searchQuery] = queryKey

  const response = await axios.get('/api/internal/items/grouped_live_search', {
    params: { query: searchQuery },
    headers: { 'Cache-Control': 'no-cache' },
    signal,
    timeout: 3000,
  })

  return response.data?.data || null
}

// Fetches query-scoped suggestions for the AI-mode search dropdown, split by product type.
// Response shape: { data: { cg: [[phrase, { thumbnailUrl, itemTitle, total }]], print_ready: [...] } }.
// Backed by react-query so each query's results are cached: closing the dropdown (blur) and reopening
// it (focus) keeps the last resolved results in place instead of refetching or flashing the curated
// columns. Returns { results, isLoading }. `results` is null until a non-empty query resolves, so the
// dropdown falls back to its curated columns. `isLoading` is true from the first keystroke of a
// non-empty query - through the 300ms debounce window and until its response settles - so the dropdown
// can show a fetching state instead of looking stale while the (slow) backend query resolves.
export default function useGroupedLiveSearch({ query = '', enabled = false } = {}) {
  const trimmedQuery = query.trim()
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery)

  const debouncedSetter = useMemo(() => debounce(setDebouncedQuery, 300), [])

  useEffect(() => {
    if (!trimmedQuery) {
      debouncedSetter.cancel()
      setDebouncedQuery('')
      return undefined
    }

    debouncedSetter(trimmedQuery)

    return () => debouncedSetter.cancel()
  }, [trimmedQuery, debouncedSetter])

  const { data, isFetching } = useQuery({
    queryKey: ['groupedLiveSearch', debouncedQuery],
    queryFn: fetchGroupedLiveSearch,
    enabled: enabled && debouncedQuery.length > 0,
    // Keep the previous query's results while the next one is in flight so the dropdown never
    // flashes the curated columns between keystrokes.
    keepPreviousData: true,
    retry: false,
  })

  const results = trimmedQuery.length > 0 ? (data ?? null) : null

  // Cover the debounce window too (the query changed but the debounced value has not caught up yet),
  // otherwise the dropdown flashes the stale/curated columns at the start of every keystroke.
  const isDebouncePending = trimmedQuery !== debouncedQuery
  const isLoading = enabled && trimmedQuery.length > 0 && (isDebouncePending || isFetching)

  return { results, isLoading }
}
